import React from 'react';
import Select from 'components/UI/Select';
import AsyncSelect from 'components/UI/AsyncSelect';
import { TableFilter } from 'components/UI/Table';
import { getSortImage } from '../utils';
import { IBreach } from 'types/breach';
import { formatUserFullName } from 'utils/helpers';

// Общие стили для заголовков колонок
export const COLUMN_HEADER_STYLE = {
	display: 'flex' as const,
	alignItems: 'center' as const,
	gap: '5px',
};

// Утилита для определения контекста страницы
export const isInBrokenPage = () => window.location.pathname.includes('broken');

/**
 * Создает стандартный заголовок колонки с сортировкой
 */
export const createSortableHeader = (
	label: string,
	sortKey: string,
	filter: TableFilter,
	sortTable: (key: string) => void,
	options?: {
		useShortLabel?: boolean;
		marginRight?: string;
	},
) => {
	const { useShortLabel = false, marginRight = '10px' } = options || {};
	const displayLabel = useShortLabel && isInBrokenPage() ? label.replace(' магазина', '') : label;

	return (
		<div style={COLUMN_HEADER_STYLE}>
			<span style={{ marginRight }}>{displayLabel}</span>
			<button onClick={() => sortTable(sortKey)}>{getSortImage(sortKey, filter.sortKey, filter.sortDirection)}</button>
		</div>
	);
};

/**
 * Создает стандартный фильтр с Select компонентом
 */
export const createSelectFilter = (
	filter: TableFilter,
	setFilter: (filter: TableFilter) => void,
	options: Array<{ value: string; label: string }>,
	filterKey: string,
	selectOptions?: {
		isClearable?: boolean;
		isDisabled?: boolean;
		defaultValue?: string;
		width?: string;
	},
) => {
	const handleChange = (newValue: string): void => {
		setFilter({ ...filter, [filterKey]: newValue });
	};

	return () => (
		<Select
			onChange={handleChange}
			options={options}
			isClearable={selectOptions?.isClearable ?? true}
			isDisabled={selectOptions?.isDisabled}
			defaultValue={selectOptions?.defaultValue}
			width={selectOptions?.width}
		/>
	);
};

/**
 * Создает стандартный фильтр для магазинов
 */
export const createStoreFilter = (
	filter: TableFilter,
	setFilter: (filter: TableFilter) => void,
	storeIdOptions: Array<{ value: string; label: string }>,
	isFilterLocked: boolean,
) => {
	const handleChange = (newValue: string): void => {
		const { storeId: _, userId: __, ...rest } = filter;
		const newFilter = newValue ? { ...rest, storeId: newValue, userId: null } : rest;
		setFilter(newFilter);
	};

	const defVal = !filter.storeId && storeIdOptions.length === 1 ? storeIdOptions[0].value : undefined;

	return () => (
		<Select
			onChange={handleChange}
			options={storeIdOptions}
			isClearable
			isDisabled={isFilterLocked}
			defaultValue={defVal}
			menuHeight="600px"
		/>
	);
};

/**
 * Создает стандартный форматер для простого текста
 */
export const createTextFormatter = (getValue: (row: IBreach) => string | undefined) => {
	return (row: IBreach) => {
		return <div>{getValue(row)}</div>;
	};
};

/**
 * Создает стандартный форматер для ФИО
 */
export const createNameFormatter = () => {
	return (row: IBreach) => {
		return (
			<div>
				{formatUserFullName(row.user)}
			</div>
		);
	};
};

/**
 * Создает фильтр для пользователей с AsyncSelect
 */
export const createUserFilter = (
	filter: TableFilter,
	setFilter: (filter: TableFilter) => void,
	userList: any[],
	loadUser: (inputValue: string) => Promise<any[]>,
	isFilterLocked: boolean,
	convertUserFilter: (users: any[]) => any[],
	isLoadingUsers?: boolean,
) => {
	const handleChange = (newValue: any): void => {
		// AsyncSelect передает value (число) или null, преобразуем null в undefined
		setFilter({ ...filter, userId: newValue ?? undefined });
	};

	const handleClick = (e: React.MouseEvent) => {
		if (!isFilterLocked && !filter.storeId) {
			e.preventDefault();
			alert('Выберите номер магазина в фильтре');
		}
	};

	// уникальный ключ на основе storeId и состояния загрузки
	const uniqueKey = `${filter.storeId || 'no-store'}-${isLoadingUsers ? 'loading' : 'loaded'}`;

	return () => (
		<div onClick={handleClick}>
			<AsyncSelect
				key={uniqueKey}
				onChange={handleChange}
				load={loadUser}
				isClearable
				defaultOptions={isLoadingUsers ? [] : convertUserFilter(userList)}
				debounceTimeout={300}
			/>
		</div>
	);
};

/**
 * Создает базовую конфигурацию колонки
 */
export const createBaseColumn = (
	value: string,
	title: string | React.ReactElement,
	formatted: (row: IBreach) => React.ReactNode,
	options?: {
		width?: string;
		filter?: () => React.ReactNode;
		visible?: boolean;
	},
) => ({
	value,
	title,
	formatted,
	...(options?.width && { width: options.width }),
	...(options?.filter && { filter: options.filter }),
	...(options?.visible !== undefined && { visible: options.visible }),
});
